import { getProcess } from '../transactions/transaction';
import { saveTransactionMessageAttachments } from './api';
import { denormalisedResponseEntities } from './data';
import { messageAttachmentIdString, uploadMessageAttachment } from './messageAttachments';

// Keep inquiry creation tied to the process alias configured on the listing.
export const inquiryParams = (listing, message = '') => {
  const processAlias = listing?.attributes?.publicData?.transactionProcessAlias;
  const transitions = processAlias ? getProcess(processAlias.split('/')[0])?.transitions : null;
  const transition = transitions?.INQUIRE || transitions?.INQUIRE_WITHOUT_PAYMENT;
  if (!transition) {
    throw new Error('This listing does not support inquiries.');
  }
  return {
    processAlias,
    transition,
    params: {
      listingId: listing.id,
      ...(transition === transitions.INQUIRE_WITHOUT_PAYMENT
        ? { protectedData: { inquiryMessage: message.trim() } }
        : {}),
    },
  };
};

export const queryTransactionMessages = (sdk, { txId, page = 1 }) =>
  sdk.messages.query({
    transactionId: txId,
    include: [
      'sender',
      'sender.profileImage',
      'publicFileAttachments',
      'publicFileAttachments.file',
    ],
    'fields.image': ['variants.square-small', 'variants.square-small2x'],
    page,
    perPage: 100,
  });

export const saveMessageAttachment = (txId, messageId, uploadedAttachment) =>
  saveTransactionMessageAttachments({
    transactionId: txId,
    attachments: [
      { ...uploadedAttachment.metadata, messageId: messageAttachmentIdString(messageId) },
    ],
  });

// Shared by the transaction page and Messages. A metadata failure happens AFTER
// delivery: report it separately so retrying the attachment never sends text twice.
export const sendTransactionMessage = async ({
  sdk,
  txId,
  message,
  attachment,
  currentUser,
  onUploadProgress,
  onSent,
}) => {
  const content = message?.trim() || '';
  if (!content && !attachment) throw new Error('Message or attachment is required.');
  const uploadedAttachment = attachment
    ? await uploadMessageAttachment({ sdk, selectedFile: attachment, onUploadProgress }).catch(
        e => {
          const error = new Error(e.message);
          error.name = 'MessageAttachmentUploadError';
          throw error;
        }
      )
    : null;
  const response = await sdk.messages.send(
    {
      transactionId: txId,
      content: content || ' ',
      ...(uploadedAttachment?.type !== 'image' && uploadedAttachment?.fileId
        ? { publicFileAttachments: [{ fileId: uploadedAttachment.fileId }] }
        : {}),
    },
    { expand: true, include: ['sender', 'publicFileAttachments', 'publicFileAttachments.file'] }
  );
  const sent = denormalisedResponseEntities(response)[0];
  const sentMessage = {
    ...sent,
    type: 'message',
    attributes: { content: content || ' ', createdAt: new Date(), ...sent.attributes },
    sender: sent.sender || currentUser,
  };
  if (onSent) onSent(sentMessage);
  let metadataResponse = null;
  let attachmentSaveError = null;
  if (uploadedAttachment?.metadata) {
    try {
      metadataResponse = await saveMessageAttachment(txId, sent.id, uploadedAttachment);
    } catch (e) {
      attachmentSaveError = e;
    }
  }
  return { message: sentMessage, uploadedAttachment, metadataResponse, attachmentSaveError };
};

export const messagesFromResponse = response => ({
  messages: denormalisedResponseEntities(response),
  pagination: response.data.meta,
});
